import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, AlertTriangle, Download, Filter, Search, X, RefreshCw,
  Clock, User, CheckCircle, XCircle, TrendingUp, Activity, Mail,
  Calendar, ChevronDown, ChevronUp, FileText, Eye
} from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { fetchWithCsrf } from '../lib/fetchWithCsrf';
import jsPDF from 'jspdf';
require('jspdf-autotable');

// Every event type the app is capable of writing.
// Source of truth: AuditEventTypes in src/lib/auditLogger.js, plus the ad-hoc
// types emitted by auditMiddleware() in that same file. Kept in sync by hand --
// anything missing here still shows up in the dropdown as long as it exists in
// the logs, because the list is unioned with what the DB actually contains.
const KNOWN_EVENT_TYPES = [
  // Authentication
  'login_success', 'login_failure', 'logout', 'password_change', 'password_reset',
  'mfa_enabled', 'mfa_disabled',
  // Authorization
  'role_change', 'permission_change', 'access_denied',
  // Data access
  'sensitive_data_access', 'bulk_data_export', 'data_deletion',
  // Tenant management
  'tenant_created', 'tenant_deleted', 'tenant_switch',
  // User management
  'user_created', 'user_deleted', 'user_invited',
  // Campaign operations
  'campaign_created', 'campaign_deleted', 'campaign_modified',
  // Lead operations
  'lead_created', 'lead_deleted', 'lead_status_change',
  // Payment
  'payment_processed', 'subscription_changed',
  // Configuration
  'settings_changed', 'api_key_created', 'api_key_generated', 'api_key_revoked',
  'webhook_configured',
  // Security
  'rate_limit_exceeded', 'invalid_signature', 'suspicious_activity',
  // Written by auditMiddleware()
  'api_request', 'lead_operation', 'campaign_operation', 'user_operation'
];

const formatEventType = (eventType) =>
  String(eventType || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const SecurityAuditLogs = () => {
  const { user } = useAuth();

  // State management
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    event_type: '',
    user_id: '',
    tenant_id: '',
    success: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Every event type ever seen in the logs. Accumulates and never shrinks, so
  // narrowing the date range does not silently remove options from the dropdown.
  const [seenEventTypes, setSeenEventTypes] = useState([]);

  // UI state
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Build query params
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (filters.event_type) params.append('event_type', filters.event_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.tenant_id) params.append('tenant_id', filters.tenant_id);
      if (filters.success !== '') params.append('success', filters.success);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetchWithCsrf(`/api/admin/audit-logs?${params}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await response.json();
      setLogs(data.logs || []);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, limit, offset]);

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetchWithCsrf(`/api/admin/audit-logs/stats?${params}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data.stats);

      // The stats endpoint groups by event_type across the whole table, so its
      // keys are the authoritative list of what is actually filterable.
      const found = Object.keys(data.stats?.byEventType || {}).filter(Boolean);
      setSeenEventTypes((prev) => {
        const merged = Array.from(new Set([...prev, ...found]));
        return merged.length === prev.length ? prev : merged;
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch recent security events
  const fetchRecentEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/admin/security-events', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch security events');
      }

      const data = await response.json();
      setRecentEvents(data.events || []);
    } catch (err) {
      console.error('Error fetching security events:', err);
    }
  }, []);

  // Any filter change resets pagination, so the effect below fires once
  const updateFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setOffset(0);
  };

  const clearFilters = () => {
    setFilters({
      event_type: '',
      user_id: '',
      tenant_id: '',
      success: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setOffset(0);
  };

  // Initial load
  useEffect(() => {
    fetchRecentEvents();
  }, [fetchRecentEvents]);

  // Load whenever filters or paging change. This runs unconditionally -- the
  // previous version only refetched when a filter was non-empty, so clearing a
  // filter back to "All Events" left the old filtered result on screen.
  useEffect(() => {
    fetchAuditLogs();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, limit, offset]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchAuditLogs(true);
        fetchRecentEvents();
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchAuditLogs, fetchRecentEvents]);

  // Export to CSV
  const exportToCSV = () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }

    try {
      const headers = ['Timestamp', 'Event Type', 'User Email', 'Tenant', 'Resource', 'Action', 'Success', 'IP Address', 'Error Message'];
      const rows = logs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.event_type,
        log.users_profile?.email || 'N/A',
        log.tenants?.name || 'N/A',
        log.resource || 'N/A',
        log.action || 'N/A',
        log.success ? 'Yes' : 'No',
        log.ip_address || 'N/A',
        log.error_message || 'N/A'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape quotes and handle special characters
          const escaped = String(cell).replace(/"/g, '""');
          return `"${escaped}"`;
        }).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `security_audit_logs_${new Date().toISOString().replace(/:/g, '-')}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('CSV export error:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (logs.length === 0) {
      alert('No logs to export');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Load logo
      const logo = new Image();
      logo.src = '/newSurFoxLogo1.png';
      await new Promise((resolve) => {
        logo.onload = resolve;
        logo.onerror = resolve; // Continue even if logo fails to load
      });

      // Header
      doc.setFillColor(79, 70, 229); // Indigo-600
      doc.rect(0, 0, pageWidth, 35, 'F');

      // Add logo if loaded
      if (logo.complete && logo.naturalHeight !== 0) {
        const logoWidth = 30;
        const logoHeight = (logo.naturalHeight / logo.naturalWidth) * logoWidth;
        doc.addImage(logo, 'PNG', pageWidth - logoWidth - 14, 5, logoWidth, Math.min(logoHeight, 25));
      }

      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Security Audit Logs Report', 14, 15);

      // Subtitle with date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${new Date().toLocaleString()}`, 14, 23);

      // Reset text color for body
      doc.setTextColor(0, 0, 0);
      let yPos = 45;

      // Summary Statistics
      if (stats) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Summary Statistics', 14, yPos);
        yPos += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const statsText = [
          `Total Events: ${stats.total.toLocaleString()}`,
          `Successful: ${stats.success.toLocaleString()}`,
          `Failed: ${stats.failed.toLocaleString()}`,
          `Success Rate: ${stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0}%`
        ];

        statsText.forEach((text, idx) => {
          doc.text(text, 14 + (idx * 45), yPos);
        });
        yPos += 12;
      }

      // Applied Filters (if any)
      const activeFilters = [];
      if (filters.event_type) activeFilters.push(`Event Type: ${filters.event_type}`);
      if (filters.success !== '') activeFilters.push(`Status: ${filters.success === 'true' ? 'Success' : 'Failed'}`);
      if (filters.start_date) activeFilters.push(`From: ${filters.start_date}`);
      if (filters.end_date) activeFilters.push(`To: ${filters.end_date}`);

      if (activeFilters.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Applied Filters:', 14, yPos);
        yPos += 6;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        activeFilters.forEach(filter => {
          doc.text(`• ${filter}`, 14, yPos);
          yPos += 5;
        });
        yPos += 5;
      }

      // Audit Logs Table
      const tableData = logs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.event_type || 'N/A',
        log.users_profile?.email || 'N/A',
        log.tenants?.name || 'N/A',
        log.success ? 'Success' : 'Failed',
        log.ip_address || 'N/A',
        (log.error_message || '').substring(0, 50) + ((log.error_message?.length > 50) ? '...' : '')
      ]);

      // Check if autoTable is available
      if (typeof doc.autoTable !== 'function') {
        console.error('autoTable is not available on jsPDF instance');
        throw new Error('PDF table plugin not loaded properly');
      }

      doc.autoTable({
        startY: yPos,
        head: [['Timestamp', 'Event Type', 'User', 'Tenant', 'Status', 'IP Address', 'Error']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [79, 70, 229], // Indigo-600
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'left'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [0, 0, 0]
        },
        alternateRowStyles: {
          fillColor: [249, 250, 251] // Gray-50
        },
        columnStyles: {
          0: { cellWidth: 32 }, // Timestamp
          1: { cellWidth: 25 }, // Event Type
          2: { cellWidth: 35 }, // User
          3: { cellWidth: 25 }, // Tenant
          4: { cellWidth: 20 }, // Status
          5: { cellWidth: 25 }, // IP
          6: { cellWidth: 30 }  // Error
        },
        margin: { left: 14, right: 14 },
        didDrawPage: function(data) {
          // Footer with page number
          doc.setFontSize(8);
          doc.setTextColor(128);
          doc.text(
            `Page ${data.pageNumber} of ${doc.internal.getNumberOfPages()}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
          );

          // Footer text
          doc.text(
            'SurFox CRM - Security Audit Report',
            14,
            pageHeight - 10
          );
        }
      });

      // Save the PDF
      const filename = `security_audit_logs_${new Date().toISOString().replace(/:/g, '-').split('.')[0]}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Delete all audit logs (for cleaning up test data)
  const deleteAllAuditLogs = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will delete ALL audit log entries from the database.\n\n' +
      'This action should ONLY be used to clean up test/bogus data.\n\n' +
      'In production, audit logs should be immutable.\n\n' +
      'Are you absolutely sure you want to proceed?'
    );

    if (!confirmed) return;

    const doubleConfirm = window.prompt(
      'Type "DELETE ALL AUDIT LOGS" to confirm (case-sensitive):'
    );

    if (doubleConfirm !== 'DELETE ALL AUDIT LOGS') {
      alert('Deletion cancelled - confirmation text did not match.');
      return;
    }

    try {
      const response = await fetchWithCsrf(
        '/api/admin/audit-logs?confirm=DELETE_ALL_AUDIT_LOGS',
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert(`✅ Successfully deleted all audit logs.`);
        fetchAuditLogs();
        fetchStats();
        fetchRecentEvents();
      } else {
        alert(`❌ Error: ${data.error}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete audit logs');
    }
  };

  // Event type badge colors
  const getEventTypeBadge = (eventType) => {
    const colors = {
      'login_success': 'bg-green-100 text-green-800',
      'login_failure': 'bg-red-100 text-red-800',
      'role_change': 'bg-purple-100 text-purple-800',
      'data_deletion': 'bg-red-100 text-red-800',
      'bulk_data_export': 'bg-yellow-100 text-yellow-800',
      'access_denied': 'bg-red-100 text-red-800',
      'rate_limit_exceeded': 'bg-orange-100 text-orange-800',
      'suspicious_activity': 'bg-red-100 text-red-800',
      'api_key_created': 'bg-blue-100 text-blue-800',
      'api_key_generated': 'bg-blue-100 text-blue-800',
      'api_key_revoked': 'bg-gray-100 text-gray-800',
      'user_created': 'bg-green-100 text-green-800',
      'user_invited': 'bg-green-100 text-green-800',
      'user_deleted': 'bg-red-100 text-red-800',
      'permission_change': 'bg-purple-100 text-purple-800',
      'password_change': 'bg-yellow-100 text-yellow-800',
      'password_reset': 'bg-yellow-100 text-yellow-800',
      'campaign_created': 'bg-blue-100 text-blue-800',
      'campaign_modified': 'bg-blue-100 text-blue-800',
      'campaign_deleted': 'bg-red-100 text-red-800',
      'settings_changed': 'bg-indigo-100 text-indigo-800',
      'payment_processed': 'bg-emerald-100 text-emerald-800',
      'subscription_changed': 'bg-emerald-100 text-emerald-800',
      'tenant_created': 'bg-green-100 text-green-800',
      'tenant_deleted': 'bg-red-100 text-red-800',
      'invalid_signature': 'bg-red-100 text-red-800'
    };
    return colors[eventType] || 'bg-gray-100 text-gray-800';
  };

  // Event types that exist in the logs, most frequent first, plus the ones the
  // app can write but has not written yet. The currently selected value is
  // always included so a filter can never become unselectable.
  const eventTypeGroups = (() => {
    const counts = stats?.byEventType || {};
    const seen = Array.from(new Set([
      ...seenEventTypes,
      ...(filters.event_type ? [filters.event_type] : [])
    ])).sort((a, b) => (counts[b]?.total || 0) - (counts[a]?.total || 0) || a.localeCompare(b));
    const unseen = KNOWN_EVENT_TYPES
      .filter((t) => !seen.includes(t))
      .sort((a, b) => a.localeCompare(b));
    return { seen, unseen, counts };
  })();

  // Filter panel component
  const FilterPanel = () => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
          <select
            value={filters.event_type}
            onChange={(e) => updateFilters({ event_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Events</option>
            {eventTypeGroups.seen.length > 0 && (
              <optgroup label="In your logs">
                {eventTypeGroups.seen.map((type) => (
                  <option key={type} value={type}>
                    {formatEventType(type)}
                    {eventTypeGroups.counts[type]?.total ? ` (${eventTypeGroups.counts[type].total})` : ''}
                  </option>
                ))}
              </optgroup>
            )}
            {eventTypeGroups.unseen.length > 0 && (
              <optgroup label="Tracked, none recorded yet">
                {eventTypeGroups.unseen.map((type) => (
                  <option key={type} value={type}>{formatEventType(type)}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.success}
            onChange={(e) => updateFilters({ success: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <div className="flex space-x-2">
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => updateFilters({ start_date: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => updateFilters({ end_date: e.target.value })}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-between items-center">
        <button
          onClick={clearFilters}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          Clear Filters
        </button>
      </div>
    </div>
  );

  // Stats cards component
  const StatsCards = () => {
    if (!stats) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Events</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total.toLocaleString()}</p>
            </div>
            <Activity className="w-10 h-10 text-indigo-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.success.toLocaleString()}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.failed.toLocaleString()}</p>
            </div>
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0}%
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-blue-500" />
          </div>
        </div>
      </div>
    );
  };

  // Recent alerts component
  const RecentAlerts = () => {
    const criticalEvents = recentEvents.filter(e =>
      !e.success ||
      ['login_failure', 'access_denied', 'suspicious_activity', 'rate_limit_exceeded'].includes(e.event_type)
    );

    if (criticalEvents.length === 0) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-sm text-green-800">No critical security events in the last 7 days</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
            <h3 className="text-sm font-semibold text-yellow-900">
              Recent Security Alerts ({criticalEvents.length})
            </h3>
          </div>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {criticalEvents.slice(0, 5).map((event, idx) => (
            <div key={idx} className="text-sm text-yellow-800 flex items-start">
              <span className="inline-block w-2 h-2 bg-yellow-600 rounded-full mt-1.5 mr-2"></span>
              <span>
                {event.event_type.replace(/_/g, ' ').toUpperCase()} - {event.user_email || 'Unknown User'} - {new Date(event.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Details modal
  const DetailsModal = () => {
    if (!selectedLog) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Audit Log Details</h2>
            <button
              onClick={() => setShowDetailsModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">Event Type</p>
                <p className="mt-1">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getEventTypeBadge(selectedLog.event_type)}`}>
                    {selectedLog.event_type}
                  </span>
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">Status</p>
                <p className="mt-1 flex items-center">
                  {selectedLog.success ? (
                    <><CheckCircle className="w-4 h-4 text-green-600 mr-1" /> Success</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-red-600 mr-1" /> Failed</>
                  )}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">Timestamp</p>
                <p className="mt-1 text-sm text-gray-900">{new Date(selectedLog.timestamp).toLocaleString()}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">User</p>
                <p className="mt-1 text-sm text-gray-900">{selectedLog.users_profile?.email || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">Tenant</p>
                <p className="mt-1 text-sm text-gray-900">{selectedLog.tenants?.name || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">IP Address</p>
                <p className="mt-1 text-sm text-gray-900">{selectedLog.ip_address || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">Resource</p>
                <p className="mt-1 text-sm text-gray-900">{selectedLog.resource || 'N/A'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-600">Action</p>
                <p className="mt-1 text-sm text-gray-900">{selectedLog.action || 'N/A'}</p>
              </div>
            </div>

            {selectedLog.user_agent && (
              <div>
                <p className="text-sm font-medium text-gray-600">User Agent</p>
                <p className="mt-1 text-sm text-gray-900 break-all">{selectedLog.user_agent}</p>
              </div>
            )}

            {selectedLog.error_message && (
              <div>
                <p className="text-sm font-medium text-gray-600">Error Message</p>
                <p className="mt-1 text-sm text-red-600">{selectedLog.error_message}</p>
              </div>
            )}

            {selectedLog.metadata && (
              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Metadata</p>
                <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">
                  {JSON.stringify(
                    typeof selectedLog.metadata === 'string'
                      ? JSON.parse(selectedLog.metadata)
                      : selectedLog.metadata,
                    null,
                    2
                  )}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-indigo-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Security Audit Logs</h1>
              <p className="text-sm text-gray-600 mt-1">Monitor and analyze security events across your platform</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                autoRefresh
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </button>

            <button
              onClick={exportToCSV}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>

            <button
              onClick={exportToPDF}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </button>

            <button
              onClick={deleteAllAuditLogs}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              title="Delete all audit logs (test data cleanup only)"
            >
              <X className="w-4 h-4 mr-2" />
              Clear Test Data
            </button>

            <button
              onClick={() => fetchAuditLogs(true)}
              disabled={refreshing}
              className="p-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Statistics */}
        <StatsCards />

        {/* Recent Alerts */}
        <RecentAlerts />

        {/* Filters */}
        {showFilters && <FilterPanel />}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">Error loading audit logs: {error}</p>
            </div>
          </div>
        )}

        {/* Logs table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Audit Log Entries</h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No audit logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resource</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getEventTypeBadge(log.event_type)}`}>
                          {log.event_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.users_profile?.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.resource ? `${log.resource}${log.action ? ` (${log.action})` : ''}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.success ? (
                          <span className="flex items-center text-sm text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Success
                          </span>
                        ) : (
                          <span className="flex items-center text-sm text-red-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            Failed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {log.ip_address || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => {
                            setSelectedLog(log);
                            setShowDetailsModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {offset + 1} to {Math.min(offset + limit, offset + logs.length)} entries
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setOffset(offset + limit)}
                  disabled={logs.length < limit}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && <DetailsModal />}
    </div>
  );
};

export default SecurityAuditLogs;
